import type { CategoryNode } from 'tradera/backend/data/category-data';
import { useEffect, useState, useCallback } from 'react';
import { filterCategories } from 'tradera/helpers/filter-categories-helper';
import { useAppSelector } from 'tradera/state/hooks';
import { useCategorySearchQuery } from 'tradera/state/services/touchweb-syi-api';
import { selectPreferredLanguageCode } from 'tradera/state/language/selectors';
import { useDebouncedState } from 'tradera/hooks/use-debounced-state';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';

export const useCategorySearch = (categories: CategoryNode[]) => {
    const [filter, setFilter] = useState('');
    const filterDebounced = useDebouncedState(filter, 200);
    const languageCodeIso2 = useAppSelector(selectPreferredLanguageCode);
    const isCategorySearchEnabled = useIsFeatureEnabled(
        'category-menu-page-search'
    );

    const [filteredCategories, setFilteredCategories] = useState<
        CategoryNode[]
    >(categories ? categories : []);

    const { data: categoryResults } = useCategorySearchQuery(
        {
            query: filterDebounced.trim(),
            searchLanguageCodeIso2: languageCodeIso2
        },
        { skip: !isCategorySearchEnabled || filterDebounced.trim().length <= 1 }
    );

    // Filter categories when changed
    useEffect(() => {
        setFilteredCategories(
            filterCategories(categories, filter.trim(), categoryResults)
        );
        // Prevent inifnite re-rendering loop as categories is re-created
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories?.[0]?.children, filter, categoryResults]);

    const handleFilterChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setFilter(event.target.value);
        },
        []
    );

    const handleFilterKey = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            const { key, target } = event;
            const inputElement = target as HTMLInputElement; // Cast event.target to HTMLInputElement
            switch (key) {
                case 'Escape':
                    event.stopPropagation();
                    if (filter.trim() === '') {
                        inputElement.blur();
                    }
                    setFilter('');
                    break;
                case 'Enter':
                    event.stopPropagation();
                    inputElement.blur();
                    break;
            }
        },
        [filter]
    );

    return {
        filter,
        setFilter,
        filteredCategories,
        handleFilterChange,
        handleFilterKey
    };
};
